import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { VenueType, PrimaryAtmosphere, Amenity } from '@wineryxplorer/shared';
import type { SortOption } from '../components/SearchPage/SortControls';

// Parse a comma-separated query param into an array (or empty array)
const parseArrayParam = (val: string | null): string[] =>
  val ? val.split(',').filter(Boolean) : [];

// Toggle an item in an array (add if absent, remove if present)
const toggleInArray = <T extends string>(arr: T[], item: T): T[] =>
  arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];

export const DEFAULT_SORT: SortOption = 'distance';

export interface UseSearchFiltersReturn {
  searchText: string;
  setSearchText: (value: string) => void;
  venueTypes: VenueType[];
  atmospheres: PrimaryAtmosphere[];
  amenities: Amenity[];
  sortOption: SortOption;
  setSortOption: (sort: SortOption) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  showMobileMap: boolean;
  setShowMobileMap: (show: boolean) => void;
  handleToggleVenueType: (v: VenueType) => void;
  handleToggleAtmosphere: (a: PrimaryAtmosphere) => void;
  handleToggleAmenity: (a: Amenity) => void;
  handleClearFilters: () => void;
  hasActiveFilters: boolean;
}

/**
 * Owns the Wineries search page's page-only URL state — free text, category
 * filters, sort, pagination, and the mobile-map flag — plus the two-way sync
 * with the query string.
 *
 * Scope params (region, city, bounds) are NOT handled here; they belong to
 * `useSearchScope`.
 */
export const useSearchFilters = (): UseSearchFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Parse URL state (page-only params) ---
  const urlQuery = searchParams.get('q') || '';
  const urlVenueTypes = parseArrayParam(searchParams.get('venueTypes')) as VenueType[];
  const urlAtmospheres = parseArrayParam(searchParams.get('atmospheres')) as PrimaryAtmosphere[];
  const urlAmenities = parseArrayParam(searchParams.get('amenities')) as Amenity[];
  const urlSort = (searchParams.get('sort') || '') as SortOption | '';
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const urlMobileMap = searchParams.get('map') === '1';

  const [searchText, setSearchText] = useState(urlQuery);
  const [venueTypes, setVenueTypes] = useState<VenueType[]>(urlVenueTypes);
  const [atmospheres, setAtmospheres] = useState<PrimaryAtmosphere[]>(urlAtmospheres);
  const [amenities, setAmenities] = useState<Amenity[]>(urlAmenities);
  const [currentPage, setCurrentPage] = useState(urlPage);
  const [sortOption, setSortOption] = useState<SortOption>((urlSort as SortOption) || DEFAULT_SORT);
  const [showMobileMap, setShowMobileMap] = useState(urlMobileMap);

  // --- Sync page-only state FROM the URL on every URL change ---
  // Scope params (region, city, bounds) are owned by the hook.
  // This effect only syncs page-only params so SavedSearchesMenu navigation works.
  useEffect(() => {
    setSearchText((prev) => (prev === urlQuery ? prev : urlQuery));
    setVenueTypes((prev) =>
      prev.length === urlVenueTypes.length && prev.every((v, i) => v === urlVenueTypes[i])
        ? prev
        : urlVenueTypes
    );
    setAtmospheres((prev) =>
      prev.length === urlAtmospheres.length && prev.every((v, i) => v === urlAtmospheres[i])
        ? prev
        : urlAtmospheres
    );
    setAmenities((prev) =>
      prev.length === urlAmenities.length && prev.every((v, i) => v === urlAmenities[i])
        ? prev
        : urlAmenities
    );
    setSortOption((prev) => {
      const next = (urlSort as SortOption) || DEFAULT_SORT;
      return prev === next ? prev : next;
    });
    setCurrentPage((prev) => (prev === urlPage ? prev : urlPage));
    setShowMobileMap((prev) => (prev === urlMobileMap ? prev : urlMobileMap));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // --- Sync state to URL ---
  const updateUrl = useCallback(
    (overrides: Record<string, string | undefined>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, val] of Object.entries(overrides)) {
        if (val) next.set(key, val);
        else next.delete(key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Debounced search text sync to URL
  useEffect(() => {
    const timeout = setTimeout(() => {
      updateUrl({
        q: searchText || undefined,
        page: '1',
      });
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Sync page-only filters/sort/page to URL whenever they change.
  useEffect(() => {
    updateUrl({
      venueTypes: venueTypes.length > 0 ? venueTypes.join(',') : undefined,
      atmospheres: atmospheres.length > 0 ? atmospheres.join(',') : undefined,
      amenities: amenities.length > 0 ? amenities.join(',') : undefined,
      sort: sortOption !== DEFAULT_SORT ? sortOption : undefined,
      page: currentPage > 1 ? String(currentPage) : undefined,
      map: showMobileMap ? '1' : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueTypes, atmospheres, amenities, sortOption, currentPage, showMobileMap]);

  // --- Handlers ---
  const handleToggleVenueType = useCallback((v: VenueType) => {
    setVenueTypes((prev) => toggleInArray(prev, v));
    setCurrentPage(1);
  }, []);

  const handleToggleAtmosphere = useCallback((a: PrimaryAtmosphere) => {
    setAtmospheres((prev) => toggleInArray(prev, a));
    setCurrentPage(1);
  }, []);

  const handleToggleAmenity = useCallback((a: Amenity) => {
    setAmenities((prev) => toggleInArray(prev, a));
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setVenueTypes([]);
    setAtmospheres([]);
    setAmenities([]);
    setCurrentPage(1);
  }, []);

  const hasActiveFilters =
    venueTypes.length > 0 || atmospheres.length > 0 || amenities.length > 0 || !!searchText;

  return {
    searchText,
    setSearchText,
    venueTypes,
    atmospheres,
    amenities,
    sortOption,
    setSortOption,
    currentPage,
    setCurrentPage,
    showMobileMap,
    setShowMobileMap,
    handleToggleVenueType,
    handleToggleAtmosphere,
    handleToggleAmenity,
    handleClearFilters,
    hasActiveFilters,
  };
};
